import * as React from "react";
import { Title } from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Stack,
  Chip,
  Avatar,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PeopleAltOutlined as PeopleIcon,
  MedicalServicesOutlined as ProIcon,
  TodayOutlined as TodayIcon,
  DateRangeOutlined as WeekIcon,
  EuroOutlined as EuroIcon,
  PendingActionsOutlined as PendingIcon,
  PersonOffOutlined as IncompleteIcon,
  FlagOutlined as FlagIcon,
  ArrowForwardRounded as ArrowIcon,
  TrendingUpRounded as UpIcon,
  TrendingDownRounded as DownIcon,
  VideocamOutlined as VisioIcon,
  StorefrontOutlined as OfficeIcon,
} from "@mui/icons-material";

import { API_BASE_URL } from "./api/config";
import { rawFetch } from "./api/httpClient";
import { BOOKING_STATUS, BOOKING_TYPE, labelOf, colorOf } from "./enums";
import { fmtDateTime, fmtMoney, fmtNumber, fullName, initialsOf, PARIS_TZ } from "./utils/format";

// ── Clés de date (heure de Paris) ───────────────────────────────────────────
const ymd = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: PARIS_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
export const todayKey = (now = new Date()) => ymd(now);
export const monthKeyOf = (now = new Date()) => ymd(now).slice(0, 7);
export const prevMonthKey = (now = new Date()) => {
  const d = new Date(now);
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return monthKeyOf(d);
};
// Lundi → dimanche de la semaine en cours.
export const weekKeys = (now = new Date()) => {
  const d = new Date(now);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d);
    x.setDate(d.getDate() + i);
    return ymd(x);
  });
};

const sum = (o) => Object.values(o || {}).reduce((a, b) => a + (Number(b) || 0), 0);

// Indicateurs dérivés du GlobalStatsDTO (GET /stats).
export const computeKpis = (s, now = new Date()) => {
  const byDay = s.bookingsByDay || {};
  const rev = s.revenueByMonth || {};
  const m = monthKeyOf(now);
  const pm = prevMonthKey(now);
  return {
    patients: s.totalPatients || 0,
    pros: s.totalProfessionals || 0,
    today: byDay[todayKey(now)] || 0,
    week: weekKeys(now).reduce((a, k) => a + (byDay[k] || 0), 0),
    revenueMonth: rev[m] || 0,
    revenuePrevMonth: rev[pm] || 0,
    totalBookings: Math.max(sum(s.bookingsByMonth), sum(byDay)),
    totalReviews: sum(s.reviewsPerPro),
    visio: s.bookingsByType?.VISIO || 0,
    office: s.bookingsByType?.OFFICE || 0,
  };
};

// ── Carte KPI ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon: Icon, accent = "primary", to, trend }) => {
  const theme = useTheme();
  const color = theme.palette[accent]?.main || theme.palette.primary.main;
  return (
    <Card
      component={to ? "a" : "div"}
      href={to}
      sx={{
        display: "block",
        height: "100%",
        textDecoration: "none",
        color: "inherit",
        transition: "border-color .15s, box-shadow .15s, transform .15s",
        ...(to && {
          "&:hover": {
            borderColor: alpha(color, 0.4),
            boxShadow: `0 6px 18px ${alpha(color, 0.12)}`,
            transform: "translateY(-1px)",
          },
        }),
      }}
    >
      <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
            {label}
          </Typography>
          <Box sx={{ width: 34, height: 34, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: alpha(color, 0.1), color, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 19 }} />
          </Box>
        </Stack>
        <Typography sx={{ fontFamily: "Sora, sans-serif", fontWeight: 700, fontSize: 28, lineHeight: 1.1, mt: 0.5, letterSpacing: "-0.02em" }}>
          {value}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, minHeight: 20 }}>
          {trend != null && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{ color: trend >= 0 ? "success.main" : "error.main", fontSize: 12, fontWeight: 700 }}
            >
              {trend >= 0 ? <UpIcon sx={{ fontSize: 16 }} /> : <DownIcon sx={{ fontSize: 16 }} />}
              <span>{`${trend >= 0 ? "+" : ""}${trend} %`}</span>
            </Stack>
          )}
          {sub && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {sub}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const SectionCard = ({ title, action, children, sx }) => (
  <Card sx={{ height: "100%", ...sx }}>
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontSize: 15 }}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const SeeAll = ({ to, children = "Tout voir" }) => (
  <Box component="a" href={to} sx={{ color: "primary.main", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    {children} <ArrowIcon sx={{ fontSize: 16 }} />
  </Box>
);

// ── Histogramme mensuel (SVG) ───────────────────────────────────────────────
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const monthLabel = (k) => (/^\d{4}-\d{2}$/.test(k) ? MONTHS[Number(k.slice(5)) - 1] : k);

// Mois courant mis en avant ; les mois futurs (RDV déjà réservés) sont ignorés.
const MonthBars = ({ data, format = fmtNumber, months = 8 }) => {
  const theme = useTheme();
  const current = monthKeyOf();
  const entries = Object.entries(data || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .filter(([key]) => !/^\d{4}-\d{2}$/.test(key) || key <= current)
    .slice(-months);
  if (!entries.length) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>Aucune donnée</Typography>;
  }
  const max = Math.max(...entries.map(([, v]) => Number(v) || 0), 1);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 170, pt: 2 }}>
        {entries.map(([k, v]) => {
          const last = k === current;
          return (
            <Box key={k} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, height: "100%", justifyContent: "flex-end" }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: last ? "primary.main" : "text.secondary", fontSize: 11, whiteSpace: "nowrap" }}>
                {format(v)}
              </Typography>
              <Box
                title={`${k} : ${format(v)}`}
                sx={{
                  width: "100%",
                  maxWidth: 38,
                  height: `${Math.max(((Number(v) || 0) / max) * 100, 2)}%`,
                  borderRadius: "8px 8px 3px 3px",
                  bgcolor: last ? "primary.main" : alpha(theme.palette.primary.main, 0.22),
                  transition: "height .3s",
                }}
              />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        {entries.map(([k]) => (
          <Typography key={k} variant="caption" sx={{ flex: 1, textAlign: "center", color: "text.secondary", fontSize: 11 }}>
            {monthLabel(k)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const HBars = ({ data, topN = 6, color }) => {
  const theme = useTheme();
  const bar = color || theme.palette.primary.main;
  const entries = Object.entries(data || {}).sort(([, a], [, b]) => b - a).slice(0, topN);
  if (!entries.length) return <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>;
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <Stack spacing={1.5}>
      {entries.map(([k, v]) => (
        <Box key={k}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{k}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmtNumber(v)}</Typography>
          </Stack>
          <Box sx={{ height: 6, bgcolor: alpha(bar, 0.1), borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ width: `${(v / max) * 100}%`, height: "100%", bgcolor: bar, borderRadius: 3 }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// ── Données « à traiter » (endpoints existants) ─────────────────────────────
const countFrom = async (path) => {
  const r = await rawFetch(`${API_BASE_URL}/${path}`);
  if (!r.ok) return null;
  const header = r.headers?.get?.("content-range");
  const xt = r.headers?.get?.("x-total-count");
  const body = await r.json().catch(() => null);
  if (header && /\/(\d+)$/.test(header)) return Number(header.match(/\/(\d+)$/)[1]);
  if (xt) return Number(xt);
  return Array.isArray(body) ? body.length : null;
};

const useActionCounts = () => {
  const [counts, setCounts] = React.useState({});
  React.useEffect(() => {
    let alive = true;
    Promise.all([
      countFrom("admin/suggestions/specialities").catch(() => null),
      countFrom("admin/suggestions/jobs").catch(() => null),
      countFrom("admin/accounts/incomplete?_page=1&_perPage=1&page=0&size=1").catch(() => null),
      countFrom("reports").catch(() => null),
      countFrom("users?type=PROFESSIONAL&hideTestUsers=true&_page=1&_perPage=1&page=0&size=1").catch(() => null),
      countFrom("reviews").catch(() => null),
    ]).then(([specs, jobs, incomplete, reports, prosTotal, reviews]) =>
      alive && setCounts({ specs, jobs, incomplete, reports, prosTotal, reviews })
    );
    return () => {
      alive = false;
    };
  }, []);
  return counts;
};

const ActionRow = ({ icon: Icon, label, count, to, accent = "warning", hint }) => {
  const theme = useTheme();
  const color = theme.palette[accent].main;
  const has = count > 0;
  return (
    <Box
      component="a"
      href={to}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.25,
        borderRadius: 3,
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ width: 36, height: 36, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: alpha(has ? color : theme.palette.text.secondary, 0.1), color: has ? color : "text.secondary" }}>
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      </Box>
      <Chip size="small" color={has ? accent : "default"} label={count == null ? "—" : fmtNumber(count)} />
      <ArrowIcon sx={{ fontSize: 18, color: "text.disabled" }} />
    </Box>
  );
};

const RecentBookings = () => {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    rawFetch(`${API_BASE_URL}/bookings?_page=1&_perPage=6&page=0&size=6`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRows(Array.isArray(d) ? d.slice(0, 6) : []))
      .catch(() => setRows([]));
  }, []);
  if (rows == null) return <Skeleton variant="rounded" height={260} />;
  if (!rows.length) return <Typography variant="body2" color="text.secondary">Aucun rendez-vous</Typography>;
  return (
    <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
      {rows.map((b, i) => (
        <Box
          key={b.id || i}
          component="a"
          href={b.id ? `#/bookings/${encodeURIComponent(b.id)}/show` : undefined}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.1, textDecoration: "none", color: "inherit", "&:hover .bk-name": { color: "primary.main" } }}
        >
          <Avatar sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700, bgcolor: "rgba(37,99,235,0.1)", color: "info.dark" }}>
            {initialsOf(b.firstNamePatient, b.lastNamePatient)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography className="bk-name" variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fullName(b.firstNamePatient, b.lastNamePatient, "Patient")}
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}> avec {fullName(b.firstNamePro, b.lastNamePro, "—")}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmtDateTime(b?.slot?.startTime)} · {labelOf(BOOKING_TYPE, b.bookingType)}
              {b.nameSpeciality ? ` · ${b.nameSpeciality}` : ""}
            </Typography>
          </Box>
          <Chip size="small" color={colorOf(BOOKING_STATUS, b.status)} label={labelOf(BOOKING_STATUS, b.status)} />
        </Box>
      ))}
    </Stack>
  );
};

const grid = (cols) => ({
  display: "grid",
  gap: 2,
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${cols}, minmax(0, 1fr))` },
});

// ── Tableau de bord ─────────────────────────────────────────────────────────
export const Dashboard = () => {
  const [stats, setStats] = React.useState(null);
  const [error, setError] = React.useState(null);
  const counts = useActionCounts();

  React.useEffect(() => {
    rawFetch(`${API_BASE_URL}/stats`)
      .then((r) => {
        if (!r.ok && r.status !== 404 && r.status !== 501) throw new Error(`HTTP ${r.status}`);
        return r.ok ? r.json() : {};
      })
      .then((d) => setStats(d || {}))
      .catch((e) => setError(e.message));
  }, []);

  const loading = stats == null && !error;
  const s = stats || {};
  const k = computeKpis(s);
  const trend = k.revenuePrevMonth > 0 ? Math.round(((k.revenueMonth - k.revenuePrevMonth) / k.revenuePrevMonth) * 100) : null;
  const typed = k.visio + k.office;
  const dateLabel = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: PARIS_TZ }).format(new Date());

  const kpi = (node) => (loading ? <Skeleton variant="rounded" height={118} sx={{ borderRadius: 4 }} /> : node);

  return (
    <Box sx={{ pb: 2 }}>
      <Title title="Tableau de bord" />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={1} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" component="h1">Tableau de bord</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, "&::first-letter": { textTransform: "uppercase" } }}>
            {dateLabel} · vue d'ensemble de l'activité ioUman
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Card sx={{ mb: 2, borderColor: "error.light" }}>
          <CardContent>
            <Typography color="error" variant="body2">Erreur de chargement des statistiques : {error}</Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ ...grid(3), mb: 2 }}>
        {kpi(<KpiCard label="Patients" value={fmtNumber(k.patients)} sub="inscrits" icon={PeopleIcon} to="#/patients" />)}
        {kpi(
          <KpiCard
            label="Professionnels actifs"
            value={fmtNumber(k.pros)}
            sub={counts.prosTotal != null ? `validés · ${fmtNumber(counts.prosTotal)} comptes au total` : "comptes validés"}
            icon={ProIcon}
            to="#/professionals"
          />
        )}
        {kpi(
          <KpiCard
            label="CA du mois"
            value={fmtMoney(k.revenueMonth)}
            sub={k.revenuePrevMonth ? `vs ${fmtMoney(k.revenuePrevMonth)} le mois dernier` : "séances non annulées"}
            trend={trend}
            icon={EuroIcon}
            accent="success"
            to="#/payments"
          />
        )}
        {kpi(<KpiCard label="RDV aujourd'hui" value={fmtNumber(k.today)} sub="programmés ce jour" icon={TodayIcon} accent="info" to="#/bookings" />)}
        {kpi(<KpiCard label="RDV cette semaine" value={fmtNumber(k.week)} sub="du lundi au dimanche" icon={WeekIcon} accent="info" to="#/bookings" />)}
        {kpi(
          <KpiCard
            label="Spécialités en attente"
            value={counts.specs == null ? "—" : fmtNumber(counts.specs)}
            sub="suggestions à valider"
            icon={PendingIcon}
            accent="warning"
            to="#/suggestions-specialities"
          />
        )}
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" }, mb: 2 }}>
        <SectionCard title="Rendez-vous par mois" action={<Chip size="small" label={`${fmtNumber(k.totalBookings)} au total`} />}>
          {loading ? <Skeleton variant="rounded" height={200} /> : <MonthBars data={s.bookingsByMonth} />}
        </SectionCard>
        <SectionCard title="À traiter">
          <Stack spacing={0.5}>
            <ActionRow icon={PendingIcon} label="Spécialités à valider" hint="Suggestions des praticiens" count={counts.specs} to="#/suggestions-specialities" />
            <ActionRow icon={PendingIcon} label="Métiers à valider" hint="Suggestions de nouveaux métiers" count={counts.jobs} to="#/suggestions-jobs" />
            <ActionRow icon={IncompleteIcon} label="Comptes incomplets" hint="Invitations / inscriptions inachevées" count={counts.incomplete} to="#/incomplete-accounts" accent="info" />
            <ActionRow icon={FlagIcon} label="Signalements" hint="Comptes signalés par la communauté" count={counts.reports} to="#/reportedUsers" accent="error" />
          </Stack>
        </SectionCard>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" }, mb: 2 }}>
        <SectionCard title="Chiffre d'affaires mensuel">
          {loading ? <Skeleton variant="rounded" height={200} /> : <MonthBars data={s.revenueByMonth} months={6} format={(v) => `${fmtNumber(Math.round(v))} €`} />}
        </SectionCard>
        <SectionCard title="Top spécialités (RDV)">
          {loading ? <Skeleton variant="rounded" height={200} /> : <HBars data={s.bookingsBySpeciality} />}
        </SectionCard>
        <SectionCard title="Mode de consultation">
          {loading ? <Skeleton variant="rounded" height={200} /> : (
            <Stack spacing={2}>
              {[
                { label: "Visio", v: k.visio, icon: VisioIcon, accent: "info" },
                { label: "Cabinet", v: k.office, icon: OfficeIcon, accent: "primary" },
              ].map((x) => (
                <Stack key={x.label} direction="row" spacing={1.5} alignItems="center">
                  <Box sx={(t) => ({ width: 36, height: 36, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: alpha(t.palette[x.accent].main, 0.1), color: `${x.accent}.main` })}>
                    <x.icon sx={{ fontSize: 19 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{x.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fmtNumber(x.v)} · {typed ? Math.round((x.v / typed) * 100) : 0} %
                      </Typography>
                    </Stack>
                    <Box sx={(t) => ({ mt: 0.75, height: 6, borderRadius: 3, bgcolor: alpha(t.palette[x.accent].main, 0.12), overflow: "hidden" })}>
                      <Box sx={{ height: "100%", width: `${typed ? (x.v / typed) * 100 : 0}%`, bgcolor: `${x.accent}.main`, borderRadius: 3 }} />
                    </Box>
                  </Box>
                </Stack>
              ))}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                <Chip size="small" label={`Âge moyen patients : ${(s.averagePatientAge || 0).toFixed(1).replace(".", ",")} ans`} />
                <Chip size="small" label={`Avis : ${fmtNumber(counts.reviews ?? k.totalReviews)}`} component="a" href="#/reviews" clickable />
              </Stack>
            </Stack>
          )}
        </SectionCard>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" } }}>
        <SectionCard title="Derniers rendez-vous" action={<SeeAll to="#/bookings" />}>
          <RecentBookings />
        </SectionCard>
        <SectionCard title="Inscriptions par mois">
          {loading ? <Skeleton variant="rounded" height={200} /> : <MonthBars data={s.registrationsByMonth} months={6} />}
        </SectionCard>
      </Box>
    </Box>
  );
};

export default Dashboard;
